import React from 'react';

import CompanyPhoneNumber from 'src/models/CompanyPhoneNumber/CompanyPhoneNumber';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import smsConfigurationResource from 'src/actions/channels/smsConfigurationResource';
import useActivateNumberForSms from 'src/presentation/components/pages/channels/shared/hooks/useActivateNumberForSms';
import { AvailablePhoneNumber, PhoneNumberType } from 'src/actions/availableNumbers/availableNumbersResource';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import {
  StepWizardActionContainer,
  StepWizardFieldWithBorder,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import {
  StyledRadioGroup,
  StyledBold,
  StyledDescription,
} from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import LocalSmsMmsNumbersOnlyAlert from './LocalSmsMmsNumbersOnlyAlert';
import { useFormChange } from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';
import { useResetFormState } from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';

const useOnSave = (number, activateNumber, nextStep) => {
  return ({ workingState }) => {
    if (!workingState.activateSms) {
      nextStep();
      return;
    }

    activateNumber(number);
  };
};

const ActivateSms = ({
  nextStep,
  saveStep,
  isActive,
  stageConfig,
  entityType,
}: {
  nextStep?;
  saveStep?;
  isActive?: boolean;
  stageConfig: any;
  entityType?;
}): JSX.Element | null => {
  const { add } = smsConfigurationResource.effects;

  const { activateNumber, isPersisting } = useActivateNumberForSms({
    add: smsConfigurationResource.effects.add,
    isActive,
    nextStep,
    saveStep,
  });

  const selectedPhone: AvailablePhoneNumber | CompanyPhoneNumber = stageConfig.selectedPhone;
  const onSaveHandler = useOnSave(selectedPhone, activateNumber, nextStep);

  const formContext = useForm({
    add,
    item: { activateSms: true },
    resetErrors: [],
    onSave: onSaveHandler,
  });

  const { onSave, workingState } = formContext;
  useFormChange(formContext.workingState, saveStep);
  useResetFormState({ activateSms: true }, formContext.updateState, stageConfig, entityType);

  const {
    selectedPhone: { phoneNumberType },
  } = stageConfig;
  const showAlert = workingState.activateSms && phoneNumberType === PhoneNumberType.LOCAL;

  return (
    <>
      <StepWizardFieldWithBorder>
        <H3 data-aid="wizard-title">Activate SMS</H3>
        <StyledDescription>
          Do you want to activate SMS capabilities for <StyledBold>{formatPhone(selectedPhone.phoneNumber)}</StyledBold>
          ?
        </StyledDescription>
        <Form context={formContext}>
          <StyledRadioGroup
            className="radioGroup"
            attr="activateSms"
            data-aid="activate-sms-radio-group"
            options={[
              {
                label: 'Yes, activate SMS',
                value: true,
                description: (
                  <>
                    Activate SMS capabilities for this number to start interacting with customers via text message.
                    {showAlert && <LocalSmsMmsNumbersOnlyAlert />}
                  </>
                ),
              },
              {
                label: 'No, don’t activate SMS',
                value: false,
                description: 'Maintain ownership of this number and reserve it for future use.',
              },
            ]}
          />
        </Form>
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button data-aid="editor-save" type={Button.Types.PRIMARY} onClick={onSave} isLoading={isPersisting}>
          Next
        </Button>
      </StepWizardActionContainer>
    </>
  );
};

export default ActivateSms;
